import {
  fetchDashboardSummary,
  fetchTickets,
  fetchTicketsByUserId,
  fetchNotRelatedTickets,
} from '../../../api/ismApi';
import {
  createTicketMappingContext,
  mapTicketResponses,
} from './ticketMapper';
import {
  invalidateMasterData,
  loadMasterData,
} from './masterDataService';

// Dashboard endpoints aggregate and enrich ticket histories. They can exceed
// the generic API client's 15-second timeout on cold or larger databases.
const DASHBOARD_REQUEST_TIMEOUT_MS = 60000;

export function serializeDashboardDateRange(dateRange) {
  if (dateRange?.mode !== 'custom'
    || !Number.isFinite(dateRange.from)
    || !Number.isFinite(dateRange.to)) {
    return {};
  }

  return {
    startDate: new Date(dateRange.from).toISOString(),
    endDate: new Date(dateRange.to).toISOString(),
  };
}

export { invalidateMasterData, loadMasterData };

function requireItems(result, resourceName) {
  if (result.status === 'rejected') {
    throw new Error(result.reason?.message || `Failed to fetch ${resourceName}.`);
  }
  if (!result.value || !Array.isArray(result.value.items)) {
    throw new Error(`${resourceName} response is invalid.`);
  }
  return result.value.items;
}

export async function loadTicketDashboardData({
  canViewAllTickets,
  userId,
  dateRange,
  signal,
  limit = 20,
  skip = 0,
  categoryRefId,
  picId,
  statusFilters,
  priorities,
  search,
  flag,
}) {
  if (!canViewAllTickets && !userId) {
    throw new Error('User session is missing. Please sign in again.');
  }

  const range = serializeDashboardDateRange(dateRange);
  const defaultRange = dateRange?.mode === 'default'
    ? {
        trendDays: 14,
        heatmapDays: 7,
      }
    : {};

  const ticketOptions = {
    ...range,
    limit,
    skip,
    categoryRefId,
    // The backend summary is scoped by JWT. Do not send the current user's
    // PIC id unless the user explicitly selected a PIC filter in the UI.
    picId: picId && picId !== 'all' ? picId : undefined,
    statuses: statusFilters,
    priorities,
    search,
    flag,
    signal,
    timeout: DASHBOARD_REQUEST_TIMEOUT_MS,
  };
  // The table request is paginated; it intentionally does not determine the
  // dashboard KPI/chart totals.
  const ticketRequest = canViewAllTickets
    ? fetchTickets(ticketOptions)
    : fetchTicketsByUserId({ userId, ...ticketOptions });
  // Keep summary independent from the paginated ticket request. The summary
  // endpoint must aggregate the full selected history, while the table only
  // receives one page of ticket documents.
  // Summary is a separate aggregate request. It can cover full history while
  // the table receives only one page of ticket documents.
  const summaryRequest = fetchDashboardSummary({
    ...range,
    ...defaultRange,
    categoryRefId,
    picId: picId && picId !== 'all' ? picId : undefined,
    statuses: statusFilters,
    priorities,
    signal,
    timeout: DASHBOARD_REQUEST_TIMEOUT_MS,
  });

  // Run independent resources together, then commit the result as one
  // dashboard snapshot in useRequests.
  const [ticketsResult, summaryResult, masterDataResult] =
    await Promise.allSettled([
      ticketRequest,
      summaryRequest,
      loadMasterData(),
    ]);

  const ticketResponse = ticketsResult.status === 'fulfilled' ? ticketsResult.value : null;
  // The ticket page and summary are intentionally committed together by the
  // hook. A late/aborted snapshot must never replace a newer one.
  const ticketDocs = requireItems(ticketsResult, 'Tickets');
  if (masterDataResult.status === 'rejected') {
    throw masterDataResult.reason;
  }
  const masterData = masterDataResult.value;
  const categories = masterData.categories || [];
  const statuses = masterData.statuses || [];
  const users = masterData.users || [];
  const categoryPicMaps = masterData.categoryPicMaps || [];
  const mappingContext = createTicketMappingContext({
    categories,
    statuses,
    users,
    categoryPicMaps,
  });

  return {
    requests: mapTicketResponses(ticketDocs, mappingContext),
    total: Number(ticketResponse?.total) || ticketDocs.length,
    pagination: {
      limit: Number(ticketResponse?.limit) || limit,
      skip: Number(ticketResponse?.skip) || skip,
      total: Number(ticketResponse?.total) || ticketDocs.length,
      hasNext: Boolean(ticketResponse?.has_next),
      hasPrevious: Boolean(ticketResponse?.has_previous),
    },
    // Keep summary failure separate from ticket failure so the table can stay
    // usable while charts show a clear degraded-data state.
    summary: summaryResult.status === 'fulfilled' ? summaryResult.value : null,
    summaryError: summaryResult.status === 'rejected'
      ? summaryResult.reason?.message || 'Failed to load dashboard summary.'
      : null,
    categories,
    users,
    categoryPicMaps,
  };
}

export async function loadDashboardSummary({
  dateRange,
  categoryRefId,
  picId,
  statusFilters,
  priorities,
  search,
  signal,
}) {
  const range = serializeDashboardDateRange(dateRange);
  const defaultRange = dateRange?.mode === 'default'
    ? { trendDays: 14, heatmapDays: 7 }
    : {};
  return fetchDashboardSummary({
    ...range,
    ...defaultRange,
    categoryRefId,
    picId: picId && picId !== 'all' ? picId : undefined,
    statuses: statusFilters,
    priorities,
    search,
    signal,
    timeout: DASHBOARD_REQUEST_TIMEOUT_MS,
  });
}

export async function loadTicketList({
  canViewAllTickets,
  userId,
  dateRange,
  signal,
  limit = 20,
  skip = 0,
  categoryRefId,
  picId,
  statusFilters,
  priorities,
  search,
  flag,
  categories = [],
  statuses = [],
  users = [],
  categoryPicMaps = [],
}) {
  if (!canViewAllTickets && !userId) {
    throw new Error('User session is missing. Please sign in again.');
  }
  const options = {
    ...serializeDashboardDateRange(dateRange),
    limit,
    skip,
    categoryRefId,
    picId: picId && picId !== 'all' ? picId : undefined,
    statuses: statusFilters,
    priorities,
    search,
    flag,
    signal,
    timeout: DASHBOARD_REQUEST_TIMEOUT_MS,
  };
  const response = canViewAllTickets
    ? await fetchTickets(options)
    : await fetchTicketsByUserId({ userId, ...options });
  const items = requireItems({ status: 'fulfilled', value: response }, 'Tickets');
  const mappingContext = createTicketMappingContext({ categories, statuses, users, categoryPicMaps });
  return {
    requests: mapTicketResponses(items, mappingContext),
    total: Number(response?.total ?? items.length),
    pagination: {
      limit: Number(response?.limit) || limit,
      skip: Number(response?.skip) || skip,
      total: Number(response?.total ?? items.length),
      hasNext: Boolean(response?.has_next),
      hasPrevious: Boolean(response?.has_previous),
    },
  };
}

export async function loadTicketPageData({
  canViewAllTickets,
  userId,
  limit = 20,
  skip = 0,
  categoryRefId,
  picId,
  statusFilters,
  priorities,
  search,
  dateRange,
  signal,
  categories = [],
  statuses = [],
  users = [],
  categoryPicMaps = [],
}) {
  if (!canViewAllTickets && !userId) {
    throw new Error('User session is missing. Please sign in again.');
  }

  const options = {
    ...serializeDashboardDateRange(dateRange),
    limit,
    skip,
    categoryRefId,
    picId: picId && picId !== 'all' ? picId : undefined,
    statuses: statusFilters,
    priorities,
    search,
    signal,
    timeout: DASHBOARD_REQUEST_TIMEOUT_MS,
  };
  const response = canViewAllTickets
    ? await fetchTickets(options)
    : await fetchTicketsByUserId({ userId, ...options });
  const items = requireItems({ status: 'fulfilled', value: response }, 'Tickets');
  const mappingContext = createTicketMappingContext({
    categories,
    statuses,
    users,
    categoryPicMaps,
  });

  return {
    requests: mapTicketResponses(items, mappingContext),
    total: Number(response?.total ?? items.length),
    pagination: {
      limit: Number(response?.limit) || limit,
      skip: Number(response?.skip) || skip,
      total: Number(response?.total ?? items.length),
      hasNext: Boolean(response?.has_next),
      hasPrevious: Boolean(response?.has_previous),
    },
  };
}

export async function loadNotRelatedTicketsData({
  limit = 20,
  skip = 0,
  signal,
} = {}) {
  const [ticketsResult, masterDataResult] =
    await Promise.allSettled([
      // The not-related list enriches/counts over the full ticket
      // collection, which has been observed taking close to (and sometimes
      // over) the generic client's 15-second timeout even at the default
      // page size. Use the same generous timeout as the other ticket-list
      // dashboard requests so a slow-but-completing query isn't aborted.
      fetchNotRelatedTickets({ limit, skip, signal, timeout: DASHBOARD_REQUEST_TIMEOUT_MS }),
      loadMasterData(),
    ]);

  const ticketDocs = requireItems(ticketsResult, 'Not-related Tickets');
  if (masterDataResult.status === 'rejected') {
    throw masterDataResult.reason;
  }
  const ticketResponse = ticketsResult.value;
  const masterData = masterDataResult.value;
  const categories = masterData.categories || [];
  const statuses = masterData.statuses || [];
  const users = masterData.users || [];
  const categoryPicMaps = masterData.categoryPicMaps || [];
  const mappingContext = createTicketMappingContext({
    categories,
    statuses,
    users,
    categoryPicMaps,
  });

  return {
    requests: mapTicketResponses(ticketDocs, mappingContext),
    categories,
    users,
    pagination: {
      limit: Number(ticketResponse?.limit) || limit,
      skip: Number(ticketResponse?.skip) || skip,
      total: Number(ticketResponse?.total ?? ticketDocs.length),
      hasNext: Boolean(ticketResponse?.has_next),
      hasPrevious: Boolean(ticketResponse?.has_previous),
    },
  };
}
